import { useEffect, useState } from 'react';
import Ajv from 'ajv';
import Alert from '@cloudscape-design/components/alert';
import Box from '@cloudscape-design/components/box';
import Button from '@cloudscape-design/components/button';
import Container from '@cloudscape-design/components/container';
import FormField from '@cloudscape-design/components/form-field';
import Header from '@cloudscape-design/components/header';
import Input from '@cloudscape-design/components/input';
import RadioGroup from '@cloudscape-design/components/radio-group';
import Select from '@cloudscape-design/components/select';
import SpaceBetween from '@cloudscape-design/components/space-between';
import StatusIndicator from '@cloudscape-design/components/status-indicator';
import Tiles from '@cloudscape-design/components/tiles';
import Textarea from '@cloudscape-design/components/textarea';
import { useApi } from '../hooks/useApi';
import { useAuth } from '../hooks/useAuth';

const ajv = new Ajv();

type Source = {
  id: string;
  display_name: string;
  source_type: string;
  configuration: Record<string, unknown>;
  secret_arn: string | null;
  enabled: boolean;
  status: string;
  last_tested_at: string | null;
  last_error_code: string | null;
};
type TestResult = { id: string; status: string; detail?: string; secret_value_read: boolean };
type McpServerCatalogEntry = {
  id: string;
  service: string;
  display_name: string;
  icon: string | null;
  config_schema: Record<string, unknown>;
  auth_kind: string;
  lifecycle_state: 'supported' | 'preview' | 'deprecated' | 'quarantined';
};

const SOURCE_TYPES = ['github', 'slack', 's3', 'manual'] as const;
type SourceType = (typeof SOURCE_TYPES)[number];

// The four hardcoded types above keep their own dedicated forms
// unchanged (design.md FR-6: additive, not a replacement). An 'mcp'
// source is registered through the catalog picker instead -- see
// mcpCatalog state and the connectorMode toggle below -- so it is
// deliberately NOT added to SOURCE_TYPES/TYPE_FIELDS/TYPE_NEEDS_SECRET;
// those stay exactly as-is for the pre-existing four types.

// Per-type field schema. Keys here must match what the backend actually
// reads: webhook registry lookups use configuration.repo / configuration.team_id
// (lambdas/webhook_github, lambdas/webhook_slack); connectivity checks use
// configuration.repo / configuration.bucket (lambdas/api_sources); the S3
// poller uses configuration.bucket + configuration.prefix, and for
// cross-account buckets configuration.role_arn (+ optional external_id)
// to AssumeRole instead of using this account's own S3 permissions
// (lambdas/s3_source_poller._client_for_source).
const TYPE_FIELDS: Record<SourceType, { key: string; label: string; placeholder: string; required: boolean }[]> = {
  github: [
    { key: 'repo', label: 'Repository (owner/name)', placeholder: 'my-org/my-repo', required: true },
  ],
  slack: [
    { key: 'team_id', label: 'Slack workspace team ID', placeholder: 'T0123456789', required: true },
  ],
  s3: [
    { key: 'bucket', label: 'S3 bucket name', placeholder: 'my-source-bucket', required: true },
    { key: 'prefix', label: 'Prefix (optional, polls this path only)', placeholder: 'docs/', required: false },
    { key: 'role_arn', label: 'Cross-account role ARN (optional, only for buckets in another AWS account)', placeholder: 'arn:aws:iam::123456789012:role/manamind-cross-account-read', required: false },
    { key: 'external_id', label: 'External ID (optional, only if the role above requires one)', placeholder: 'manamind-s3-source', required: false },
  ],
  manual: [],
};

const TYPE_NEEDS_SECRET: Record<SourceType, boolean> = {
  github: true,
  slack: true,
  s3: false,
  manual: false,
};

function emptyFieldValues(type: SourceType): Record<string, string> {
  return Object.fromEntries(TYPE_FIELDS[type].map((f) => [f.key, '']));
}

export function SourcesPage() {
  const { apiFetch } = useApi();
  const { user } = useAuth();
  const [sources, setSources] = useState<Source[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [name, setName] = useState('');
  const [type, setType] = useState<SourceType>('github');
  const [fieldValues, setFieldValues] = useState<Record<string, string>>(emptyFieldValues('github'));
  const [secretArn, setSecretArn] = useState('');
  const [testResults, setTestResults] = useState<Record<string, TestResult>>({});
  const isAdmin = user?.role === 'knowledge-source-admin' || user?.role === 'platform-admin';

  // FR-6: catalog connectors (mcp-backed) vs. the four pre-existing
  // hardcoded types, as a top-level choice. 'manual' keeps its own
  // label distinct from 'catalog' -- picking a connector TYPE within
  // manual mode still uses the existing Select/TYPE_FIELDS UI below.
  const [connectorMode, setConnectorMode] = useState<'manual' | 'catalog'>('manual');
  const [mcpCatalog, setMcpCatalog] = useState<McpServerCatalogEntry[]>([]);
  const [selectedMcpServerId, setSelectedMcpServerId] = useState<string | null>(null);
  const [mcpScopeJson, setMcpScopeJson] = useState('{}');
  const [mcpScopeError, setMcpScopeError] = useState<string | null>(null);
  const [discoveredCandidates, setDiscoveredCandidates] = useState<{ label: string; value: string }[] | null>(null);
  const [selectedCandidateValue, setSelectedCandidateValue] = useState<string | null>(null);
  const [discoverFailed, setDiscoverFailed] = useState(false);
  const [discoverLoading, setDiscoverLoading] = useState(false);

  const load = async () => {
    try {
      const result = await apiFetch<{ sources: Source[] }>('/sources');
      setSources(result.sources);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to load sources');
    }
  };

  const loadMcpCatalog = async () => {
    try {
      const result = await apiFetch<{ servers: McpServerCatalogEntry[] }>('/mcp-servers');
      setMcpCatalog(result.servers);
    } catch (err) {
      // A catalog-fetch failure must not block the four existing
      // connector types from working -- degrade to an empty catalog
      // (the catalog picker simply has nothing to show) rather than
      // surfacing a page-level error over the whole form.
      setMcpCatalog([]);
    }
  };

  useEffect(() => {
    if (isAdmin) {
      void load();
      void loadMcpCatalog();
    }
  }, [isAdmin]);

  if (!isAdmin) {
    return (
      <Alert type="warning">
        Knowledge source administration requires the knowledge-source-admin role.
      </Alert>
    );
  }

  const changeType = (nextType: SourceType) => {
    setType(nextType);
    setFieldValues(emptyFieldValues(nextType));
  };

  const selectedMcpServer = mcpCatalog.find((entry) => entry.id === selectedMcpServerId) ?? null;
  // service_account-auth_kind servers (google-drive-v1, migration 008)
  // authenticate via a single PLATFORM-scoped secret the admin never
  // supplies per-source (manamind-prod/platform-oauth/<service> --
  // see mcp_broker/handler.py's _platform_service_account_key_for) --
  // requiring a secret_arn here would only ever produce one that fails
  // handler.py's SECRET_ARN regex (manamind-prod/source-* only), which
  // is exactly the bug this guard exists to prevent. oauth2_refresh/
  // bearer servers (linear-v1) still need their own per-tenant secret.
  const selectedMcpServerNeedsSecret = selectedMcpServer ? selectedMcpServer.auth_kind !== 'service_account' : true;

  const missingRequiredField = TYPE_FIELDS[type].find((f) => f.required && !fieldValues[f.key]?.trim());
  const missingRequiredSecret = TYPE_NEEDS_SECRET[type] && !secretArn.trim();
  const missingMcpServerSelection = connectorMode === 'catalog' && !selectedMcpServer;
  const missingMcpSecret = connectorMode === 'catalog' && selectedMcpServerNeedsSecret && !secretArn.trim();
  const canSubmit = connectorMode === 'catalog'
    ? name.trim().length > 0 && !missingMcpServerSelection && !missingMcpSecret && !mcpScopeError
    : name.trim().length > 0 && !missingRequiredField && !missingRequiredSecret;

  const create = async () => {
    try {
      if (connectorMode === 'catalog' && selectedMcpServer) {
        let scope: unknown;
        try {
          scope = JSON.parse(mcpScopeJson);
        } catch {
          setMcpScopeError('Scope must be valid JSON');
          return;
        }
        await apiFetch('/sources', {
          method: 'POST',
          body: {
            display_name: name,
            source_type: 'mcp',
            configuration: { mcp_server_id: selectedMcpServer.id, scope },
            secret_arn: secretArn || null,
          },
        });
        setName('');
        setSelectedMcpServerId(null);
        setMcpScopeJson('{}');
        setSecretArn('');
        await load();
        return;
      }

      const configuration = Object.fromEntries(
        Object.entries(fieldValues).filter(([, value]) => value.trim().length > 0),
      );
      await apiFetch('/sources', {
        method: 'POST',
        body: { display_name: name, source_type: type, configuration, secret_arn: secretArn || null },
      });
      setName('');
      setFieldValues(emptyFieldValues(type));
      setSecretArn('');
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to create source');
    }
  };

  const action = async (id: string, actionName: 'test' | 'enable' | 'disable') => {
    try {
      const result = await apiFetch<TestResult>(`/sources/${id}/${actionName}`, { method: 'POST' });
      if (actionName === 'test') setTestResults((prev) => ({ ...prev, [id]: result }));
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Source action failed');
    }
  };

  const secretNameSuggestion = connectorMode === 'catalog' && selectedMcpServer
    ? `manamind-prod/source-mcp/${selectedMcpServer.service}/${
        name.trim() ? name.trim().toLowerCase().replace(/[^a-z0-9]+/g, '-') : 'my-source'
      }`
    : `manamind-prod/source-${type}-${
        name.trim() ? name.trim().toLowerCase().replace(/[^a-z0-9]+/g, '-') : 'my-source'
      }`;

  const validateMcpScope = (value: string) => {
    setMcpScopeJson(value);
    let parsed: unknown;
    try {
      parsed = JSON.parse(value);
    } catch {
      setMcpScopeError('Scope must be valid JSON');
      return;
    }
    if (selectedMcpServer) {
      const validate = ajv.compile(selectedMcpServer.config_schema);
      if (!validate(parsed)) {
        const firstError = validate.errors?.[0];
        setMcpScopeError(firstError ? `${firstError.instancePath || 'scope'} ${firstError.message}` : 'Scope does not match this connector\'s schema');
        return;
      }
    }
    setMcpScopeError(null);
  };

  const discover = async () => {
    if (!selectedMcpServer) return;
    setDiscoverLoading(true);
    setDiscoverFailed(false);
    try {
      // scope carries service_account-kind servers' non-secret config
      // (google-drive-v1's impersonate_email, migration 008) -- Discover
      // runs BEFORE any knowledge_sources row exists, so this is the
      // only way that value reaches the broker at Discover time (see
      // api_sources.handler._discover_mcp_scope's own comment on why
      // this is validated as object-shape only, not against the full
      // config_schema, which would otherwise reject exactly the
      // fields Discover exists to help find).
      let scope: unknown = {};
      try {
        scope = JSON.parse(mcpScopeJson);
      } catch {
        setMcpScopeError('Scope must be valid JSON');
        return;
      }
      const result = await apiFetch<{ candidates: { label: string; value: string }[] }>('/sources/discover', {
        method: 'POST',
        body: { mcp_server_id: selectedMcpServer.id, secret_arn: secretArn || null, scope },
      });
      if (result.candidates.length === 0) {
        // An empty candidate list is ambiguous (genuinely nothing found
        // vs. discovery not actually implemented for this transport
        // yet -- Task 4's disclosed container-build gap) -- degrade to
        // the raw-JSON fallback rather than showing an empty picker
        // that looks broken.
        setDiscoverFailed(true);
        setDiscoveredCandidates(null);
        return;
      }
      setDiscoveredCandidates(result.candidates);
      setSelectedCandidateValue(null);
    } catch (err) {
      setDiscoverFailed(true);
      setDiscoveredCandidates(null);
    } finally {
      setDiscoverLoading(false);
    }
  };

  const applyDiscoveredCandidate = (value: string) => {
    setSelectedCandidateValue(value);
    // The scope shape is service-specific (design.md's config_schema
    // per registry row) -- this UI does not know which property name
    // the selected value belongs under, so it hands the admin a
    // pre-filled starting point in the JSON editor rather than
    // guessing a key name. Re-validated through the same ajv path as
    // manual JSON entry.
    validateMcpScope(JSON.stringify({ selected: value }, null, 2));
  };

  return (
    <SpaceBetween size="l">
      <Header
        variant="h1"
        description="Register connectors that feed documents into Manamind. Manamind never stores or displays credential values — only a reference to where they live in AWS Secrets Manager."
      >
        Knowledge Sources
      </Header>

      {error && <Alert type="error">{error}</Alert>}

      <Container header={<Header variant="h2">Register a new source</Header>}>
        <SpaceBetween size="m">
          <FormField label="Display name">
            <Input
              value={name}
              onChange={({ detail }) => setName(detail.value)}
              placeholder="e.g. Product Docs Repo"
            />
          </FormField>

          <FormField label="Connector source">
            <Tiles
              value={connectorMode}
              onChange={({ detail }) => setConnectorMode(detail.value as 'manual' | 'catalog')}
              items={[
                { value: 'manual', label: 'GitHub, Slack, S3, or manual', description: 'The original four connector types, configured directly.' },
                { value: 'catalog', label: 'Catalog connector (MCP)', description: mcpCatalog.length > 0 ? `${mcpCatalog.length} service${mcpCatalog.length === 1 ? '' : 's'} available` : 'No catalog services available yet' },
              ]}
            />
          </FormField>

          {connectorMode === 'catalog' ? (
            <SpaceBetween size="m">
              <FormField label="Service">
                <RadioGroup
                  value={selectedMcpServerId}
                  onChange={({ detail }) => {
                    setSelectedMcpServerId(detail.value);
                    // A different service has a different config_schema
                    // -- reset scope/discovery state rather than
                    // validating stale JSON against the new schema.
                    setMcpScopeJson('{}');
                    setMcpScopeError(null);
                    setDiscoveredCandidates(null);
                    setSelectedCandidateValue(null);
                    setDiscoverFailed(false);
                  }}
                  items={mcpCatalog.map((entry) => ({
                    value: entry.id,
                    label: entry.lifecycle_state === 'preview' ? `${entry.display_name} (preview)` : entry.display_name,
                  }))}
                />
              </FormField>

              {selectedMcpServer && (
                <>
                  {selectedMcpServerNeedsSecret ? (
                    <Alert type="info" header="Before you register this source">
                      <SpaceBetween size="xs">
                        <Box>
                          Create a secret in AWS Secrets Manager containing this service's credential.
                          The secret name must start with <Box variant="code">manamind-prod/source-mcp/</Box>,
                          for example:
                        </Box>
                        <Box variant="code">{secretNameSuggestion}</Box>
                        <Box>
                          Then paste the secret's full ARN below. Manamind reads this secret only to test
                          connectivity and process events — it is never shown in this UI or returned by the
                          API.
                        </Box>
                      </SpaceBetween>
                    </Alert>
                  ) : (
                    <Alert type="info" header="No per-source secret needed">
                      <Box>
                        {selectedMcpServer.display_name} authenticates via a single, platform-level credential
                        an administrator already configured for the whole workspace — you don't provide a
                        secret here. Use the scope field below to specify which account/resource this source
                        should access (see the connector's schema for the required field).
                      </Box>
                    </Alert>
                  )}

                  {selectedMcpServerNeedsSecret && (
                    <FormField label="Secrets Manager ARN *">
                      <Input
                        value={secretArn}
                        onChange={({ detail }) => setSecretArn(detail.value)}
                        placeholder="arn:aws:secretsmanager:us-east-1:940702769152:secret:manamind-prod/source-mcp/..."
                      />
                    </FormField>
                  )}

                  <SpaceBetween size="xs" direction="horizontal">
                    <Button
                      onClick={() => void discover()}
                      disabled={(selectedMcpServerNeedsSecret && !secretArn.trim()) || discoverLoading}
                      loading={discoverLoading}
                    >
                      Discover
                    </Button>
                    <Box color="text-body-secondary" fontSize="body-s">
                      See what this credential can access, instead of typing an identifier.
                    </Box>
                  </SpaceBetween>

                  {discoveredCandidates && discoveredCandidates.length > 0 && (
                    <FormField label="Select what to ingest">
                      <RadioGroup
                        value={selectedCandidateValue}
                        onChange={({ detail }) => applyDiscoveredCandidate(detail.value)}
                        items={discoveredCandidates.map((candidate) => ({ value: candidate.value, label: candidate.label }))}
                      />
                    </FormField>
                  )}

                  {discoverFailed && (
                    <Alert type="warning" header="Discovery unavailable">
                      Manamind could not discover what this credential can see (the connector may not
                      support discovery yet). Enter the scope manually as JSON below.
                    </Alert>
                  )}

                  <FormField
                    label="Scope (JSON)"
                    description="What this connector should read, e.g. a Linear team key or a Drive folder ID."
                    errorText={mcpScopeError}
                  >
                    <Textarea
                      value={mcpScopeJson}
                      onChange={({ detail }) => validateMcpScope(detail.value)}
                      placeholder='{"team_key": "ENG"}'
                      rows={3}
                    />
                  </FormField>
                </>
              )}
            </SpaceBetween>
          ) : (
            <>
              {TYPE_NEEDS_SECRET[type] && (
                <Alert type="info" header="Before you register this source">
                  <SpaceBetween size="xs">
                    <Box>
                      Create a secret in AWS Secrets Manager containing the connector credential (a
                      GitHub personal access token, or a Slack bot token). The secret name must start
                      with <Box variant="code">manamind-prod/source-</Box>, for example:
                    </Box>
                    <Box variant="code">{secretNameSuggestion}</Box>
                    <Box>
                      Then paste the secret's full ARN below. Manamind reads this secret only to test
                      connectivity and process events — it is never shown in this UI or returned by the
                      API.
                    </Box>
                  </SpaceBetween>
                </Alert>
              )}

              <FormField label="Connector type">
                <Select
                  selectedOption={{ value: type, label: type }}
                  onChange={({ detail }) => changeType(detail.selectedOption.value as SourceType)}
                  options={SOURCE_TYPES.map((value) => ({ value, label: value }))}
                />
              </FormField>

              {TYPE_FIELDS[type].map((field) => (
                <FormField key={field.key} label={field.required ? `${field.label} *` : field.label}>
                  <Input
                    value={fieldValues[field.key] ?? ''}
                    onChange={({ detail }) => setFieldValues((prev) => ({ ...prev, [field.key]: detail.value }))}
                    placeholder={field.placeholder}
                  />
                </FormField>
              ))}

              {type === 'manual' && (
                <Box color="text-body-secondary">
                  Manual sources have no connector configuration. Documents are added directly through
                  the Author page and tagged with this source for tracking.
                </Box>
              )}

              {TYPE_NEEDS_SECRET[type] && (
                <FormField label="Secrets Manager ARN *">
                  <Input
                    value={secretArn}
                    onChange={({ detail }) => setSecretArn(detail.value)}
                    placeholder="arn:aws:secretsmanager:us-east-1:940702769152:secret:manamind-prod/source-..."
                  />
                </FormField>
              )}
              {type === 's3' && (
                <FormField label="Secrets Manager ARN (optional — only for cross-account buckets using static credentials)">
                  <Input
                    value={secretArn}
                    onChange={({ detail }) => setSecretArn(detail.value)}
                    placeholder="Leave blank if the bucket is in this account, or if using the Cross-account role ARN above"
                  />
                </FormField>
              )}
            </>
          )}

          <SpaceBetween size="xs">
            <Button variant="primary" disabled={!canSubmit} onClick={() => void create()}>
              Register source
            </Button>
            {!canSubmit && (name.trim() || missingRequiredField || missingRequiredSecret || missingMcpServerSelection || missingMcpSecret || mcpScopeError) && (
              <Box color="text-status-warning" fontSize="body-s">
                {!name.trim()
                  ? 'Display name is required.'
                  : connectorMode === 'catalog'
                    ? missingMcpServerSelection
                      ? 'Select a service from the catalog.'
                      : missingMcpSecret
                        ? 'Secrets Manager ARN is required for this connector.'
                        : mcpScopeError ?? undefined
                    : missingRequiredField
                      ? `${missingRequiredField.label} is required.`
                      : 'Secrets Manager ARN is required for this connector type.'}
              </Box>
            )}
          </SpaceBetween>
        </SpaceBetween>
      </Container>

      <SpaceBetween size="s">
        {sources.length === 0 && (
          <Box color="text-body-secondary">No sources registered yet.</Box>
        )}
        {sources.map((source) => (
          <Container
            key={source.id}
            header={
              <Header
                variant="h3"
                description={`${source.source_type} · ${source.enabled ? source.status : 'disabled'}`}
                actions={
                  <SpaceBetween direction="horizontal" size="xs">
                    <Button onClick={() => void action(source.id, 'test')}>Validate</Button>
                    <Button onClick={() => void action(source.id, source.enabled ? 'disable' : 'enable')}>
                      {source.enabled ? 'Pause' : 'Enable'}
                    </Button>
                  </SpaceBetween>
                }
              >
                {source.display_name}
              </Header>
            }
          >
            <SpaceBetween size="xs">
              {Object.keys(source.configuration).length > 0 && (
                <Box color="text-body-secondary" fontSize="body-s">
                  {Object.entries(source.configuration)
                    .map(([key, value]) => `${key}: ${value}`)
                    .join(' · ')}
                </Box>
              )}
              {source.last_tested_at && (
                <Box color="text-body-secondary" fontSize="body-s">
                  Last validated: {source.last_tested_at}
                </Box>
              )}
              {testResults[source.id] && (
                <StatusIndicator type={testResults[source.id].status === 'configured' ? 'success' : 'error'}>
                  {testResults[source.id].detail}
                </StatusIndicator>
              )}
            </SpaceBetween>
          </Container>
        ))}
      </SpaceBetween>
    </SpaceBetween>
  );
}
